import {Component, inject, signal, Signal} from '@angular/core';
import {Apollo} from 'apollo-angular';
import {RouterModule} from '@angular/router';

import {LogoAreaComponent} from '../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';
import {ContextService} from '../../services/context.service';
import {Organization} from '../../types-repository';
import {GET_PORTALS} from '../../queries-signiant';
import {Portal, SessionStatus} from '../../types-signiant';
import {DateComponent} from '../../date/date.component';

/** A portal as the API returns it. The lazily resolved `browse`/`processes`
 *  fields are deliberately not selected here (each costs a live portal call). */
type PortalRow = Portal;

/**
 * One table line, already resolved.
 *
 * The direction-specific reads happen here in TypeScript, where `__typename`
 * actually narrows, rather than in the template — Angular's template checking
 * does not narrow a union's members, so a template reaching for
 * `settings.autoExport` on an acquisition portal would compile and silently
 * render nothing.
 */
export interface PortalListRow {
  id: string;
  name: string;
  portalUrl: string;
  lastSweepAt?: string | null;
  status?: SessionStatus | null;
  /** What the "What we do" column shows — a portal can do both.
   *
   *  Named from our side of the transfer, matching the rest of the page: a
   *  portal we fetch from is a Download, one we send to is an Upload. The
   *  detail page's two halves and its Recent Downloads / Recent Uploads lists
   *  use the same two words, so an operator never has to translate between
   *  "acquisition" and "download". */
  kind: 'Download' | 'Upload' | 'Both';
  /** Tooltip for that column. */
  kindHint: string;
}

function toRow(portal: PortalRow): PortalListRow {
  // Presence of a capability is what it means to have it, so both can be set.
  const fetches = !!portal.download;
  const sends = !!portal.upload;
  const kind = fetches && sends ? 'Both' : sends ? 'Upload' : 'Download';
  const hint = fetches && sends
    ? 'We download from this portal and upload to it'
    : sends ? 'We upload to this portal' : 'We download from this portal';
  return {
    id: portal.id, name: portal.name, portalUrl: portal.portalUrl,
    lastSweepAt: portal.lastSweepAt, status: portal.status,
    kind, kindHint: hint,
  };
}

@Component({
  selector: 'app-content-acquisition-portals',
  templateUrl: './portals.component.html',
  styleUrls: ['./portals.component.scss'],
  imports: [LogoAreaComponent, HeadlineSectionComponent, RouterModule, DateComponent]
})
export class PortalsComponent {
  organization: Signal<Organization>;
  portals = signal<PortalListRow[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);

  private apollo = inject(Apollo);

  constructor(contextService: ContextService) {
    this.organization = contextService.organization;
    // network-only: a portal's session status is live state, and a cached
    // "logged in" would be actively misleading.
    this.apollo.use('signiant')
      .query<{ portals: PortalRow[] }>({query: GET_PORTALS, fetchPolicy: 'network-only'})
      .subscribe({
        next: ({data}) => {
          this.portals.set((data?.portals ?? []).map(toRow));
          this.loading.set(false);
        },
        error: (err) => {
          this.error.set(err?.message ?? String(err));
          this.loading.set(false);
        }
      });
  }
}
